import React, { useContext } from 'react';
import CourseDetail from './CourseDetail';
import CoursePlayer from './CoursePlayer';
import Home from './Home';
import Header from './Header';
import Footer from './Footer';
import LoginForm from './LoginForm';
import StudentDashboard from './StudentDashboard';
import AdminDashboard from './AdminDashboard';
import CourseManagement from './CourseManagement';
import NewCourseForm from './NewCourseForm';
import UdemyInstructorDashboard from './UdemyInstructorDashboard';
import InstructorRegisterForm from './InstructorRegisterForm';
import InstructorProfile from './InstructorProfile';
import Messages from './Messages';
import StudentProfile from './StudentProfile';
import StudentRegisterForm from './StudentRegisterForm';
import ForgotPasswordForm from './ForgotPasswordForm';
import MarketingInfoPage from './MarketingInfoPage';
import { AuthContext } from './AuthContext';

const AppRouter = () => {
  const [currentView, setCurrentView] = React.useState('home');
  const [selectedCourseId, setSelectedCourseId] = React.useState(null);
  const [selectedCategory, setSelectedCategory] = React.useState(null);

  const { currentUser, isAuthenticated, logout, quickAccess } = useContext(AuthContext);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const accessRole = params.get('access');
    const portal = params.get('portal');

    if (portal === 'admin') {
      setCurrentView('loginAdmin');
      params.delete('portal');
      const nextSearch = params.toString();
      const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`;
      window.history.replaceState({}, '', nextUrl);
      return;
    }

    if (!accessRole || !['student'].includes(accessRole)) {
      return;
    }

    const applyQuickAccess = async () => {
      const result = await quickAccess(accessRole);

      if (result?.success && result.user) {
        setCurrentView(
          result.user.role === 'admin'
            ? 'adminDashboard'
            : result.user.role === 'instructor'
              ? 'instructorDashboard'
              : 'studentDashboard'
        );
      }

      params.delete('access');
      const nextSearch = params.toString();
      const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`;
      window.history.replaceState({}, '', nextUrl);
    };

    applyQuickAccess();
  }, [quickAccess]);

  const handleLogout = () => {
    logout();
    setCurrentView('home');
  };

  const handleSelectCourse = (courseId) => {
    setSelectedCourseId(courseId);
    setCurrentView('learnCourse');
  };

  const handleSelectCategory = (categoryName) => {
    setSelectedCategory(categoryName);
    setCurrentView('home');
  };

  const renderHome = () => (
    <Home
      onNavigate={setCurrentView}
      onSelectCourse={setSelectedCourseId}
      selectedCategory={selectedCategory}
      onSelectCategory={handleSelectCategory}
    />
  );

  const renderView = () => {
    switch (currentView) {
      case 'accessDirectory':
        return (
          <LoginForm
            onLoginSuccess={(user) => setCurrentView(
              user?.role === 'admin'
                ? 'adminDashboard'
                : user?.role === 'instructor'
                  ? 'instructorDashboard'
                  : 'studentDashboard'
            )}
            onNavigate={setCurrentView}
            profile="general"
          />
        );
      case 'login':
        return (
          <LoginForm
            onLoginSuccess={(user) => setCurrentView(
              user?.role === 'admin'
                ? 'adminDashboard'
                : user?.role === 'instructor'
                  ? 'instructorDashboard'
                  : 'studentDashboard'
            )}
            onNavigate={setCurrentView}
            profile="general"
          />
        );
      case 'loginStudent':
        return (
          <LoginForm
            onLoginSuccess={() => setCurrentView('studentDashboard')}
            onNavigate={setCurrentView}
            profile="student"
          />
        );
      case 'loginInstructor':
        return (
          <LoginForm
            onLoginSuccess={() => setCurrentView('instructorDashboard')}
            onNavigate={setCurrentView}
            profile="instructor"
          />
        );
      case 'loginAdmin':
        return (
          <LoginForm
            onLoginSuccess={() => setCurrentView('adminDashboard')}
            onNavigate={setCurrentView}
            profile="admin"
          />
        );
      case 'registerInstructor':
        return (
          <InstructorRegisterForm
            onRegisterSuccess={() => setCurrentView('instructorDashboard')}
            onCancel={() => setCurrentView('home')}
          />
        );
      case 'business':
        return (
          <MarketingInfoPage
            eyebrow="Soluciones para equipos"
            title="EduDigital Business para empresas, instituciones y equipos"
            description="Centraliza capacitacion, seguimiento y desarrollo profesional con rutas de aprendizaje listas para Bolivia y equipos de cualquier tamaño."
            highlights={[
              {
                badge: '01',
                title: 'Capacitacion centralizada',
                description: 'Agrupa cursos por area, crea rutas internas y mide avance por colaborador o por equipo.'
              },
              {
                badge: '02',
                title: 'Reportes claros',
                description: 'Visualiza progreso, cursos terminados y categorias con mayor impacto para tomar decisiones.'
              },
              {
                badge: '03',
                title: 'Implementacion rapida',
                description: 'Activa cuentas, asigna formacion y comienza con un catalogo listo para usar desde el primer dia.'
              }
            ]}
            primaryAction={{ label: 'Ir al inicio', onClick: () => setCurrentView('home') }}
            secondaryAction={{ label: 'Ver cursos', onClick: () => setCurrentView('home') }}
          />
        );
      case 'teachOnEduDigital':
        return (
          <MarketingInfoPage
            eyebrow="Programa de instructores"
            title="Enseña en EduDigital y convierte tu experiencia en cursos"
            description="Publica tus clases, organiza recursos, mide estadisticas y genera ingresos con una experiencia pensada para instructores."
            highlights={[
              {
                badge: 'A',
                title: 'Publica a tu ritmo',
                description: 'Crea secciones, sube videos y agrega recursos como GitHub, Drive o archivos para cada clase.'
              },
              {
                badge: 'B',
                title: 'Mide tu impacto',
                description: 'Consulta estadisticas, alumnos finalizados y la comision estimada por curso desde tu panel.'
              },
              {
                badge: 'C',
                title: 'Empieza hoy',
                description: 'Registra tu cuenta de instructor y lanza tu primer curso con el flujo guiado de la plataforma.'
              }
            ]}
            primaryAction={{ label: 'Crear cuenta de instructor', onClick: () => setCurrentView('registerInstructor') }}
            secondaryAction={{ label: 'Volver al inicio', onClick: () => setCurrentView('home') }}
          />
        );
      case 'registerStudent':
        return (
          <StudentRegisterForm
            onRegisterSuccess={() => setCurrentView('studentDashboard')}
            onNavigate={setCurrentView}
            onCancel={() => setCurrentView('home')}
          />
        );
      case 'home':
        return renderHome();
      case 'forgotPassword':
        return (
          <ForgotPasswordForm
            onNavigate={setCurrentView}
            onResetPassword={(email) => {
              console.log(`Enviar email de recuperacion a: ${email}`);
            }}
          />
        );
      case 'courseDetail':
        return <CourseDetail courseId={selectedCourseId} onNavigate={setCurrentView} />;
      case 'learnCourse':
        if (!selectedCourseId) {
          setCurrentView('home');
          return null;
        }
        return (
          <div className="container mx-auto px-4 py-8">
            <CoursePlayer courseId={selectedCourseId} onNavigate={setCurrentView} />
          </div>
        );
      case 'instructorDashboard':
        if (!isAuthenticated || currentUser?.role !== 'instructor') {
          return <LoginForm onLoginSuccess={() => setCurrentView('instructorDashboard')} />;
        }
        return <UdemyInstructorDashboard onNavigate={setCurrentView} onLogout={handleLogout} />;
      case 'studentDashboard':
        if (!isAuthenticated || currentUser?.role !== 'student') {
          return <LoginForm onLoginSuccess={() => setCurrentView('studentDashboard')} />;
        }
        return <StudentDashboard onNavigate={setCurrentView} onLogout={handleLogout} onSelectCourse={handleSelectCourse} />;
      case 'adminDashboard':
        if (!isAuthenticated || currentUser?.role !== 'admin') {
          return <LoginForm onLoginSuccess={() => setCurrentView('adminDashboard')} />;
        }
        return <AdminDashboard onNavigate={setCurrentView} onLogout={handleLogout} />;
      case 'manageCourses':
        if (!isAuthenticated || currentUser?.role !== 'instructor') {
          return <LoginForm onLoginSuccess={() => setCurrentView('manageCourses')} />;
        }
        return <CourseManagement onBack={() => setCurrentView('instructorDashboard')} />;
      case 'createNewCourse':
        if (!isAuthenticated || currentUser?.role !== 'instructor') {
          return <LoginForm onLoginSuccess={() => setCurrentView('createNewCourse')} />;
        }
        return <NewCourseForm onCancel={() => setCurrentView('instructorDashboard')} onSave={() => setCurrentView('instructorDashboard')} />;
      case 'messages':
        return <Messages onNavigate={setCurrentView} />;
      case 'profile':
        if (!isAuthenticated) {
          return <LoginForm onLoginSuccess={() => setCurrentView('profile')} />;
        }
        return currentUser?.role === 'instructor'
          ? <InstructorProfile onNavigate={setCurrentView} />
          : <StudentProfile onNavigate={setCurrentView} />;
      default:
        return renderHome();
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header
        onNavigate={setCurrentView}
        onSelectCategory={handleSelectCategory}
        isAuthenticated={isAuthenticated}
        isInstructor={currentUser?.role === 'instructor'}
        onLogout={handleLogout}
      />
      <main className="flex-grow">
        {renderView()}
      </main>
      <Footer />
    </div>
  );
};

export default AppRouter;
